import { useMemo, useState } from "react";
import styles from "./SequenceSettings.module.css";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import { useToast } from "../../../context/ToastContext";
import SequenceModal from "../../../components/System&Settings/SequenceSettings/SequenceModal";
import SequenceAuditModal from "../../../components/System&Settings/SequenceSettings/SequenceAuditModal";
import { useGetSequenceTypes } from "../../../api/sytem&settings/useSequenceTypes";
import {
  useCreateSequence,
  useGetSequences,
  useUpdateSequence,
} from "../../../api/sytem&settings/useSequences";

// ── Type meta ────────────────────────────────────────────────
const TYPE_META = {
  BOOKING:     { label: "Booking",     color: "blue",   icon: "📋" },
  INVOICE:     { label: "Invoice",     color: "green",  icon: "🧾" },
  CREDIT_NOTE: { label: "Credit Note", color: "orange", icon: "📝" },
  RECEIPT:     { label: "Receipt",     color: "purple", icon: "🧾" },
};

function buildSequenceCode(prefix, numericLength, currentValue) {
  const number = Number.isFinite(Number(currentValue)) ? Number(currentValue) : 0;
  const padded = String(number).padStart(Number(numericLength) || 4, "0");
  return `${prefix || "XX"}-${padded}`;
}

function formatDateTimeDisplay(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).replace("T", " ").replace(".000Z", "");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

function formatUpdatedBy(value) {
  if (!value) return "Super Admin";
  if (typeof value === "number") return "Super Admin";
  if (/^\d+$/.test(String(value))) return "Super Admin";
  return String(value);
}

export default function SequenceSettings() {
  const { showToast } = useToast();
  const { sequenceTypes, resetTypes } = useGetSequenceTypes();
  const { execute: createSequence, loading: createLoading } = useCreateSequence();
  const { execute: updateSequence, loading: updateLoading } = useUpdateSequence();
  const [modalOpen, setModalOpen]     = useState(false);
  const [auditOpen, setAuditOpen]     = useState(false);
  const [editRow, setEditRow]         = useState(null);
  const [auditRow, setAuditRow]       = useState(null);
  const [filterType, setFilterType]   = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const queryParams = useMemo(() => ({ page: 1, limit: 200 }), []);
  const {
    data: sequences,
    loading: sequencesLoading,
    error: sequencesError,
    refetch: refetchSequences,
  } = useGetSequences(queryParams);
  const filterTypeOptions = useMemo(() => {
    if (sequenceTypes.length > 0) return sequenceTypes;
    return Object.entries(TYPE_META).map(([value, meta]) => ({
      value,
      label: meta.label,
    }));
  }, [sequenceTypes]);

  const filtered = sequences.filter((s) => {
    if (filterType   && s.type   !== filterType)             return false;
    if (filterStatus && String(s.status) !== filterStatus)   return false;
    return true;
  });

  const handleAdd = () => { setEditRow(null); setModalOpen(true); };

  const handleEdit = (row) => { setEditRow(row); setModalOpen(true); };

  const handleToggleStatus = async (row) => {
    try {
      await updateSequence(row.id, { ...row, status: !row.status });
      showToast("Sequence status updated", "success");
      refetchSequences();
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to update sequence status",
        "error"
      );
    }
  };

  const handleSave = async (payload) => {
    try {
      if (editRow) {
        await updateSequence(editRow.id, payload);
        showToast("Sequence updated successfully", "success");
      } else {
        await createSequence(payload);
        showToast("Sequence created successfully", "success");
      }
      setModalOpen(false);
      refetchSequences();
    } catch (error) {
      showToast(
        error.response?.data?.errors?.[0] ||
          error.response?.data?.message ||
          "Failed to save sequence",
        "error"
      );
    }
  };

  const handleOpenAudit = (row) => { setAuditRow(row); setAuditOpen(true); };

  return (
    <div className={styles.page}>

      {/* ── Page Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <div className={styles.pageTitle}>Sequence Settings</div>
          <div className={styles.pageCrumb}>System Settings › Sequence Settings</div>
        </div>
        <button className={styles.btnPrimary} onClick={handleAdd}>
          + Add Sequence
        </button>
      </div>

      {/* ── Info banner ── */}
      <div className={styles.banner}>
        <span>⚠️</span>
        <span>
          Sequences control numbering for bookings, invoices, credit notes, and
          receipts. Numbers are applied automatically — never editable at
          booking or document level. Changes affect <strong>future documents only</strong>.
        </span>
      </div>

      {/* ── Filters ── */}
      <div className={styles.filterBar}>
        <select
          className={styles.filterSelect}
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">All Types</option>
          {filterTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          className={styles.filterSelect}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <span className={styles.filterCount}>
          Showing {filtered.length} of {sequences.length} sequences
        </span>
      </div>

      {/* ── Cards grid ── */}
      <div className={styles.cardsGrid}>
        {sequencesLoading && (
          <div className={styles.empty}>Loading sequences...</div>
        )}
        {!sequencesLoading && sequencesError && (
          <div className={styles.empty}>{sequencesError}</div>
        )}
        {!sequencesLoading && !sequencesError && filtered.map((seq) => (
          <SequenceCard
            key={seq.id}
            seq={seq}
            onEdit={() => handleEdit(seq)}
            onToggle={() => handleToggleStatus(seq)}
            onAudit={() => handleOpenAudit(seq)}
          />
        ))}
        {!sequencesLoading && !sequencesError && filtered.length === 0 && (
          <div className={styles.empty}>No sequences match your filters.</div>
        )}
      </div>

      {/* ── Modals ── */}
      <SequenceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        initialData={editRow}
        sequenceTypeOptions={filterTypeOptions}
        resetTypeOptions={resetTypes.length ? resetTypes : undefined}
        isSaving={createLoading || updateLoading}
      />
      <SequenceAuditModal
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        sequence={auditRow}
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SEQUENCE CARD
══════════════════════════════════════════════════════════════ */
function SequenceCard({ seq, onEdit, onToggle, onAudit }) {
  const meta    = TYPE_META[seq.type] || {};
  const code = buildSequenceCode(seq.prefix, seq.numericLength, seq.currentValue);

  return (
    <div className={`${styles.card} ${!seq.status ? styles.cardInactive : ""}`}>

      {/* Card header */}
      <div className={styles.cardHead}>
        <div className={styles.cardHeadLeft}>
          <span className={`${styles.typeIcon} ${styles[`typeIcon_${meta.color}`]}`}>
            {meta.icon}
          </span>
          <div>
            <div className={styles.cardName}>{seq.name}</div>
            <span className={`${styles.typeBadge} ${styles[`typeBadge_${meta.color}`]}`}>
              {meta.label}
            </span>
          </div>
        </div>
        <ToggleSwitch checked={seq.status} onChange={onToggle} />
      </div>

      {/* Preview number */}
      <div className={styles.previewBox}>
        <span className={styles.previewLabel}>Current code</span>
        <span className={styles.previewNumber}>{code}</span>
      </div>

      {/* Stat row */}
      <div className={styles.statRow}>
        <div className={styles.stat}>
          <span className={styles.statKey}>Prefix</span>
          <span className={styles.statVal}>{seq.prefix}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statKey}>Digits</span>
          <span className={styles.statVal}>{seq.numericLength}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statKey}>Current</span>
          <span className={styles.statVal}>{seq.currentValue.toLocaleString()}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statKey}>Reset</span>
          <span className={styles.statVal}>{seq.resetType}</span>
        </div>
      </div>

      {/* Flags row */}
      <div className={styles.flagsRow}>
        <span className={`${styles.flag} ${seq.lockedAfterUse ? styles.flagOn : styles.flagOff}`}>
          {seq.lockedAfterUse ? "🔒 Locked after use" : "🔓 Not locked"}
        </span>
        <span className={`${styles.flag} ${seq.systemWide ? styles.flagOn : styles.flagOff}`}>
          {seq.systemWide ? "🌐 System-wide" : "📄 Limited scope"}
        </span>
      </div>

      {/* Footer */}
      <div className={styles.cardFooter}>
        <span className={styles.cardUpdated}>
          Updated {formatDateTimeDisplay(seq.updatedAt)} by {formatUpdatedBy(seq.updatedBy)}
        </span>
        <div className={styles.cardActions}>
          <button className={styles.btnAudit} onClick={onAudit}>Log</button>
          <button className={styles.btnEdit}  onClick={onEdit}>Edit</button>
        </div>
      </div>
    </div>
  );
}